import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IReservation, IReservationExtra, IReservationRequestWindow, IUser, PaymentStatus } from '@curbnturf/entities';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'admin/reservation';

export interface IAdminRequestWindow extends IReservationRequestWindow {
  adminExtendedBy?: IUser | null;
}

export interface IAdminModifyInput {
  date: { from: string; to: string };
  rvId?: number;
  extras?: IReservationExtra[];
  refundMethod?: 'card' | 'cash';
  note?: string;
}

export interface IAdminModifyQuote {
  originalTotal: number;
  newTotal: number;
  /** Positive: charged to the guest's card. Negative: refunded. */
  difference: number;
  hostNetDelta: number;
  /** Most that can go back to the card; any more is refunded as CNT Cash. */
  cardRefundable: number;
}

export interface IAdminCommunicationChannel {
  id: number;
  kind: 'reservation' | 'pre-reservation' | 'other-reservation';
  reservationId?: number | null;
  timestamp?: number;
  messages: {
    id: number;
    fromId?: number;
    toId?: number;
    fromName?: string;
    body: string;
    timestamp: number;
    read: boolean;
  }[];
}

export interface IAdminLedgerRow {
  id: number;
  timestamp: number;
  userId: number;
  event: string;
  value: number;
  note: string;
  privateNote: string;
  status: string;
}

export interface IAdminReservationTransactions {
  payments: {
    purchaseId: string;
    timestamp: number;
    amount: number;
    transactionId: string;
    originalTransactionId?: string;
    status: PaymentStatus;
  }[];
  cash: IAdminLedgerRow[];
  accountBalance: IAdminLedgerRow[];
}

/** Admin-only reservation actions and views (`/admin/reservation`, AdminAuthGuard). */
@Injectable({ providedIn: 'root' })
export class AdminReservationApiService {
  private readonly http = inject(HttpClient);

  requestWindows(id: number): Observable<IAdminRequestWindow[]> {
    return this.http.get<IAdminRequestWindow[]>(`${API_URL}/${id}/request-windows`);
  }

  extend(id: number, hours: number, note?: string): Observable<IAdminRequestWindow> {
    return this.http.post<IAdminRequestWindow>(`${API_URL}/${id}/extend`, { hours, note });
  }

  accept(id: number, reason?: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${id}/accept`, { reason });
  }

  reject(id: number, reason?: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${id}/reject`, { reason });
  }

  /** Omit `refundAmount` to apply the listing's cancellation policy. */
  cancel(id: number, reason?: string, refundAmount?: number): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${id}/cancel`, { reason, refundAmount });
  }

  quoteModify(id: number, input: IAdminModifyInput): Observable<IAdminModifyQuote> {
    return this.http.post<IAdminModifyQuote>(`${API_URL}/${id}/modify/quote`, input);
  }

  modify(id: number, input: IAdminModifyInput): Observable<IReservation> {
    return this.http.put<IReservation>(`${API_URL}/${id}/modify`, input);
  }

  communications(id: number): Observable<IAdminCommunicationChannel[]> {
    return this.http.get<IAdminCommunicationChannel[]>(`${API_URL}/${id}/communications`);
  }

  transactions(id: number): Observable<IAdminReservationTransactions> {
    return this.http.get<IAdminReservationTransactions>(`${API_URL}/${id}/transactions`);
  }
}
